/**
 * KYP API Service
 * Handles all API calls related to KYP functionality
 */

// Helper function to get base URL
const getBaseUrl = () => import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;

// Function to create request headers with auth token
const createHeaders = (token: string) => ({
  'Authorization': `Bearer ${token}`,
  'Content-Type': 'application/json'
});

/**
 * Fetch player matches by name
 */
export const fetchPlayerMatches = async (playerName: string, token: string) => {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/ai/v1/players/ids-for-name?name=${encodeURIComponent(playerName)}`, {
    credentials: 'include',
    headers: createHeaders(token)
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
};

/**
 * Search players with detailed information
 * @param query The search query
 * @param token The authentication token
 * @param limit Optional limit for number of results (default: 20)
 */
export const searchPlayers = async (query: string, token: string, limit: number = 20) => {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/ai/v1/player/name_search?query=${encodeURIComponent(query)}&limit=${limit}`, {
    headers: createHeaders(token)
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
};

/**
 * Check for automated search results
 */
export const checkAutomatedResults = async (playerId: string, token: string) => {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/ai/v1/player/${playerId}/automated_search_results`, {
    headers: createHeaders(token)
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
};

/**
 * Fetch player profile
 */
export const fetchPlayerProfile = async (playerId: string, token: string) => {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/ai/v1/player/${playerId}/profile`, {
    credentials: 'include',
    headers: createHeaders(token)
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
};

/**
 * Fetch player injuries
 */
export const fetchPlayerInjuries = async (playerId: string, token: string) => {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/ai/v1/player/${playerId}/injuries`, {
    headers: createHeaders(token)
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
};

/**
 * Fetch player transfers
 */
export const fetchPlayerTransfers = async (playerId: string, token: string) => {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/ai/v1/player/${playerId}/transfers`, {
    headers: createHeaders(token)
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
};

/**
 * Fetch player achievements
 */
export const fetchPlayerAchievements = async (playerId: string, token: string) => {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/ai/v1/player/${playerId}/achievements`, {
    headers: createHeaders(token)
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
};

/**
 * Fetch player stats
 */
export const fetchPlayerStats = async (playerId: string, token: string) => {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/ai/v1/player/${playerId}/stats`, {
    headers: createHeaders(token)
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
};

/**
 * Start async search
 */
export const startAsyncSearch = async (playerId: string, playerName: string, token: string) => {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/ai/v1/player/search/async`, {
    method: 'POST',
    headers: createHeaders(token),
    body: JSON.stringify({ 
      player_id: playerId,
      player_name: playerName,
      force_refresh: true
    })
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
};

/**
 * Check search status
 */
export const checkSearchStatus = async (searchId: number, token: string) => {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/ai/v1/player/search/async/${searchId}/status`, {
    headers: createHeaders(token)
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
};

/**
 * Get search results
 */
export const getSearchResults = async (searchId: number, token: string) => {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/ai/v1/player/search/async/${searchId}/results`, {
    headers: createHeaders(token)
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
};

/**
 * Cancel search
 */
export const cancelSearch = async (searchId: number, token: string) => {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/ai/v1/player/search/async/${searchId}`, {
    method: 'DELETE',
    headers: createHeaders(token)
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
};

/**
 * Fetch agent info
 */
export const fetchAgentInfo = async (playerId: string, token: string) => {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/ai/v1/player/${playerId}/agent-info`, {
    headers: createHeaders(token)
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
};

/**
 * Fetch historical search results
 */
export const fetchHistoricalSearchResults = async (searchId: number, token: string) => {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/ai/v1/player/search/async/${searchId}/results`, {
    headers: createHeaders(token)
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
};